import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import orderBy from 'lodash/orderBy';
import { selectFacilityId } from 'src/store/configSlice';
import { DeviceAllocation, useGetLatestDeviceActivityQuery } from 'src/store/sageAdminApi';
import {
    getDevicesFromAllocation,
    getDevicesFromAllocations,
} from 'src/utils/getDevicesFromAllocation';

interface useAllocationsWithTimestampsProps {
    deviceAllocations?: DeviceAllocation[];
    queryOptions?: {
        pollingInterval?: number;
        refetchOnMountOrArgChange?: boolean;
        refetchOnReconnect?: boolean;
    };
}

function useAllocationsWithTimestamps(props: useAllocationsWithTimestampsProps) {
    const { deviceAllocations, queryOptions = {} } = props;
    const { pollingInterval, refetchOnMountOrArgChange, refetchOnReconnect } = queryOptions;

    const facilityId = useSelector(selectFacilityId);
    const { devices } = getDevicesFromAllocations(deviceAllocations || []);

    const deviceIds = useMemo(
        () =>
            devices?.map((device) => device?.id).filter((id): id is string => id !== undefined) ||
            [],
        [devices]
    );

    const { data: devicePressTimestampMap, ...rest } = useGetLatestDeviceActivityQuery(
        {
            getLatestDeviceActivityRequest: {
                facilityRid: facilityId,
                deviceRids: deviceIds ?? [],
            },
        },
        {
            skip: !facilityId || !deviceIds || !deviceIds.length,
            pollingInterval,
            refetchOnMountOrArgChange,
            refetchOnReconnect,
        }
    );

    const deviceAllocationsWithTimestamps = useMemo(() => {
        if (!devicePressTimestampMap || !deviceAllocations) {
            return deviceAllocations;
        }

        return orderBy(
            deviceAllocations.map((deviceAllocation) => {
                const { devices } = getDevicesFromAllocation(deviceAllocation);
                const devicesWithTimestamps = devices?.map((device) => {
                    if (device?.id && devicePressTimestampMap[device.id]) {
                        const deviceWithTimestamp = devicePressTimestampMap[device.id];
                        let lastPressTimestamp: string;

                        // This supports both possible formats of timestamp data in the device activity response
                        // TODO: Replace with only the object format once the battery level has been deployed.
                        if (
                            typeof deviceWithTimestamp === 'object' &&
                            deviceWithTimestamp !== null &&
                            'latestActivityTimestamp' in deviceWithTimestamp
                        ) {
                            lastPressTimestamp = `${
                                (deviceWithTimestamp as { latestActivityTimestamp: number })
                                    .latestActivityTimestamp
                            }`;
                        } else {
                            lastPressTimestamp = `${deviceWithTimestamp}`;
                        }

                        return { ...device, lastPressTimestamp };
                    } else {
                        return device;
                    }
                });

                return { ...deviceAllocation, devices: devicesWithTimestamps } as DeviceAllocation;
            }),
            [
                (deviceAllocation) => {
                    // Sort the allocation by the most recent timestamp from the devices array
                    const { devices } = getDevicesFromAllocation(deviceAllocation);
                    const timestamps =
                        devices
                            ?.map((device) => device?.lastPressTimestamp)
                            .filter((ts): ts is string => Boolean(ts)) || [];
                    return timestamps.length > 0
                        ? Math.max(...timestamps.map((ts) => parseInt(ts, 10)))
                        : 0;
                },
            ],
            ['desc']
        );
    }, [deviceAllocations, devicePressTimestampMap]);

    return {
        ...rest,
        deviceIds,
        deviceAllocations: deviceAllocationsWithTimestamps,
    };
}

export default useAllocationsWithTimestamps;
